import React, { useMemo, useState } from "react";
import { IPicture, ICategory } from "../../models";
import {
  IonCard,
  IonCardTitle,
  IonCardSubtitle,
  IonIcon,
  IonText,
  IonCardContent,
  IonGrid,
  IonRow,
  IonCol,
  IonButton,
  IonChip,
  IonLabel,
  IonModal,
} from "@ionic/react";
import { Link } from "react-router-dom";
import {
  accessibility,
  alarm,
  alarmOutline,
  cart as cartIcon,
  cartOutline,
  closeOutline,
  copy,
  earth,
  ellipse,
  flowerOutline,
  heart,
  heartHalfOutline,
  heartOutline,
  imageOutline,
  images,
  informationCircleOutline,
  mail,
  paw,
  sparkles,
  tabletLandscape,
} from "ionicons/icons";
import { connect } from "react-redux";
import { AppState } from "../main.reducer";
import {
  CHANGE_CART,
  CHANGE_RESERVE,
  SET_PICTURE_FRAMES,
} from "../Cart/cart.actions";
import { IPictureFrames } from "../Cart/cart.reducer";
import { SET_FAVORITES, SELECT_PICTURE } from "./pictures.actions";
import { SELECT_CATEGORY } from "../Menu/menu.actions";
import { PackageContentModal } from "../PackageContent/PackageContentModal";
import { Price } from "../../components/Price/Price";
import { PriceRow } from "../../components/Price/PriceRow";
import { DiscountBadge } from "../../components/DiscountBadge/DicsountBadge";
import { Flex } from "../../components/Flex/Flex";
import { Currency, KIDS_CATEGORY } from "../../common/constants";
import { getEquipmentPrice } from "../../common/utils";
import { NumberMoney } from "../../components/NumberMoney/NumberMoney";
import { DifficultyDots } from "../../components/DifficultyDots/DifficultyDots";
import { getDifficultyLevel } from "../../common/difficulty";

type OwnProps = {
  pic?: IPicture;
  isPopup?: boolean;
  /** /slika/:id - like the popup, shows the difficulty description */
  isDetail?: boolean;
};

const mapStateToProps = (state: AppState, props: OwnProps) => ({
  availableOnly: state.pictures.availableOnly,
  favorites: state.pictures.favorites,
  cart: state.cart.cart,
  reserve: state.cart.reserve,
  categories: state.menu.categories as ICategory[],
  darkMode: state.common.darkMode,
  currency: state.common.currency as Currency,
  allPictures: state.pictures.allPictures as IPicture[],
  pictureFrames: state.cart.pictureFrames as IPictureFrames,
});

const mapDispatchToProps = (dispatch: (action: any) => any) => ({
  selectPicture: (picture: IPicture) =>
    dispatch({ type: SELECT_PICTURE, picture }),
  setFavorites: (favorites: number[]) => {
    dispatch({ type: SET_FAVORITES, favorites });
  },
  selectCategory: (category: ICategory) =>
    dispatch({ type: SELECT_CATEGORY, category }),
  changeCart: (
    cart: { [pictureId: number]: number },
    pictureId: number,
    quantity: number,
  ) => {
    const cartState = { ...cart, [pictureId]: quantity };
    localStorage.setItem("bpb.cart", JSON.stringify(cartState));
    dispatch({ type: CHANGE_CART, pictureId, quantity });
  },
  changeReserve: (
    reserve: { [pictureId: number]: number },
    pictureId: number,
    quantity: number,
  ) => {
    const reserveState = { ...reserve, [pictureId]: quantity };
    localStorage.setItem("bpb.reserve", JSON.stringify(reserveState));
    dispatch({ type: CHANGE_RESERVE, pictureId, quantity });
  },
  setPictureFrames: (pictureFrames: IPictureFrames) => {
    localStorage.setItem("bpb.pictureFrames", JSON.stringify(pictureFrames));
    dispatch({ type: SET_PICTURE_FRAMES, pictureFrames });
  },
});

export const IconCategory = (props: { category: number }) => {
  switch (props.category) {
    case -1:
      return <IonIcon icon={sparkles} color="dark" />;
    case 0:
      return <IonIcon icon={images} color="dark" />;
    case 1:
      return <IonIcon icon={imageOutline} color="dark" />;
    case 3:
      return <IonIcon icon={paw} color="dark" />;
    case 4:
      return <IonIcon icon={flowerOutline} color="dark" />;
    case 5:
      return <IonIcon icon={heartHalfOutline} color="dark" />;
    case 6:
      return <IonIcon icon={earth} color="dark" />;
    case 7:
      return <IonIcon icon={accessibility} color="dark" />;
    default:
      return <></>;
  }
};

type AllProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  OwnProps;

const PictureCard = (props: AllProps) => {
  const { pic, isPopup, favorites, cart, reserve, setFavorites, currency } =
    props;
  const difficultyLevel = getDifficultyLevel(pic?.difficulty);

  const [showPackageModal, setShowPackageModal] = useState<boolean>(false);
  const isKids = pic?.category === KIDS_CATEGORY;
  const selectedFrame = isKids ? "none" : "";
  const [showFrameSheet, setShowFrameSheet] = useState<boolean>(false);
  const [showBlindRamModal, setShowBlindRamModal] = useState<boolean>(false);
  const [frameSheetScrolled, setFrameSheetScrolled] = useState(false);
  const [blindRamScrolled, setBlindRamScrolled] = useState(false);

  const equipmentPrice = useMemo(
    () => getEquipmentPrice(props.allPictures),
    [props.allPictures],
  );

  // Assembled frame: only for motifs marked for it, and not for BiH
  const canHaveFrameAssembled = !!pic?.frameAssembled && currency !== "KM";

  const addToCartWithFrame = (frameChoice: string) => {
    if (!pic) return;
    props.changeCart(cart, pic.id, cart[pic.id] + 1 || 1);
    const current = props.pictureFrames[pic.id] || { frames: 0, assembled: 0 };
    if (frameChoice === "disassembled") {
      props.setPictureFrames({
        ...props.pictureFrames,
        [pic.id]: { ...current, frames: current.frames + 1 },
      });
    } else if (frameChoice === "assembled") {
      props.setPictureFrames({
        ...props.pictureFrames,
        [pic.id]: { ...current, assembled: current.assembled + 1 },
      });
    }
  };

  const favoriteToggle: (id: number) => void = (id: number) => {
    const newFavorites: number[] =
      favorites.indexOf(id) < 0
        ? [...favorites, id]
        : favorites.filter((f) => f !== id);
    setFavorites(newFavorites);
    localStorage.setItem(
      "bpb.pictures.favorites",
      JSON.stringify(newFavorites),
    );
  };

  const categoryName = (id: number) => {
    const category = props.categories.find((c) => c.id === id);
    return category ? category.name : "";
  };

  const findCategory = (id: number) => {
    return props.categories.find((c) => c.id === id) || ({} as ICategory);
  };

  const isFavorite = useMemo(
    () => (id: number) => favorites.indexOf(id) >= 0,
    [favorites],
  );

  if (!pic) {
    return <></>;
  }

  return (
    <IonCard style={{ margin: "3px" }}>
      <div style={{ position: "relative" }}>
        <picture style={{ cursor: isPopup ? "auto" : "pointer" }}>
          <source
            media="(min-width:650px)"
            srcSet={`assets/pics/${pic.code}.jpg`}
          />
          <img
            src={`assets/pics/320/${pic.code}.jpg`}
            alt={pic.fileName}
            onClick={() => props.selectPicture(pic)}
            style={{ width: "100%" }}
            loading="lazy"
          />
        </picture>
        <IonIcon
          color={isFavorite(pic.id) ? "danger" : "dark"}
          icon={isFavorite(pic.id) ? heart : heartOutline}
          onClick={() => favoriteToggle(pic.id)}
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            fontSize: 28,
            cursor: "pointer",
            filter: "drop-shadow(0 0 2px rgba(0,0,0,0.4))",
          }}
        />
      </div>
      <DiscountBadge
        price={pic.price}
        priceNoDiscount={pic.priceNoDiscount}
        isPopup={isPopup}
      />

      {!isPopup && (
        <IonCardTitle
          className="ion-text-center f20xs"
          style={{ padding: "5px 0" }}
        >
          {pic.title}
        </IonCardTitle>
      )}

      <IonCardContent style={{ padding: 5 }}>
        <IonGrid>
          <PriceRow
            status={
              <IonCardSubtitle>
                <IonIcon
                  icon={ellipse}
                  style={{
                    paddingRight: 5,
                    color: pic.status
                      ? "var(--ion-color-success-shade)"
                      : "var(--ion-color-danger-shade)",
                  }}
                />
                <IonText>
                  {pic.status ? "Na stanju" : "Trenutno nema na stanju"}
                </IonText>
              </IonCardSubtitle>
            }
            price={
              <Price
                price={pic.price}
                priceNoDiscount={pic.priceNoDiscount}
                isPopup={isPopup}
              />
            }
          />
        </IonGrid>

        <Flex css={{ padding: "5px 2%" }}>
          {pic.sizeX && pic.sizeY && (
            <IonText>
              {`Dimenzije (š x v):`}
              <br />
              {`${pic.sizeX} x ${pic.sizeY}cm`}
            </IonText>
          )}
          <div style={{ flexGrow: 1 }} />
          <IonText style={{ textAlign: "right" }}>
            {"Šifra:"}
            <br />
            {pic.code}
          </IonText>
        </Flex>

        {difficultyLevel && (
          <div style={{ padding: "0 2% 5px" }}>
            <Flex css={{ alignItems: "center", flexWrap: "wrap", gap: 6 }}>
              <IonText>Nivo težine:</IonText>
              <DifficultyDots value={difficultyLevel.value} />
              <IonText>{difficultyLevel.label}</IonText>
            </Flex>
            {(isPopup || props.isDetail) && (
              <IonText
                className="f13"
                style={{ color: "var(--ion-color-medium)" }}
              >
                {difficultyLevel.description}
              </IonText>
            )}
          </div>
        )}

        <Flex
          css={{
            alignItems: "center",
            flexDirection: "column",
            justifyContent: "center",
            margin: "0 auto",
            width: "fit-content",
          }}
        >
          {isPopup && (
            <Link
              to={{ pathname: `/slike/${findCategory(pic.category).code}` }}
              style={{ textDecoration: "none", color: "var(--ion-color-dark)" }}
              target="_self"
              onClick={() => props.selectCategory(findCategory(pic.category))}
            >
              <IonChip>
                <IconCategory category={pic.category} />
                <IonLabel>{categoryName(pic.category)}</IonLabel>
              </IonChip>
            </Link>
          )}

          <Flex>
            <IonChip onClick={() => setShowPackageModal(true)}>
              <IonIcon icon={mail} color="dark"></IonIcon>
              <IonLabel>Sadržaj seta</IonLabel>
            </IonChip>
          </Flex>
        </Flex>

        <IonGrid>
          {isKids && (
            <IonRow>
              <IonCol size="12">
                <IonChip style={{ width: "100%", justifyContent: "center" }}>
                  <IonLabel>Na blind ramu</IonLabel>
                  <IonIcon icon={copy} color="dark" />
                </IonChip>
              </IonCol>
            </IonRow>
          )}
          <IonRow>
            <IonCol size="12" offset="0">
              {pic.status && (
                <IonButton
                  color="success"
                  disabled={cart[pic.id] > 0}
                  expand="block"
                  onClick={() => {
                    if (selectedFrame === "") {
                      setShowFrameSheet(true);
                    } else {
                      addToCartWithFrame(selectedFrame);
                    }
                  }}
                >
                  <IonIcon
                    icon={cart[pic.id] > 0 ? cartIcon : cartOutline}
                    size="large"
                    slot="start"
                  />
                  {cart[pic.id] > 0 ? "U korpi" : "U korpu"}
                </IonButton>
              )}
              {!pic.status && (
                <IonButton
                  color="warning"
                  disabled={reserve[pic.id] > 0}
                  expand="block"
                  onClick={() =>
                    props.changeReserve(
                      reserve,
                      pic.id,
                      reserve[pic.id] + 1 || 1,
                    )
                  }
                >
                  <IonIcon
                    icon={reserve[pic.id] > 0 ? alarm : alarmOutline}
                    size="large"
                    slot="start"
                  />
                  {reserve[pic.id] > 0 ? "Rezervisano" : "Rezerviši"}
                </IonButton>
              )}
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonCardContent>
      <IonModal
        isOpen={showFrameSheet}
        onDidDismiss={() => setShowFrameSheet(false)}
        className="cart-modal"
      >
        <div
          style={{
            overflowY: "auto",
            maxHeight:
              "calc(90dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
          }}
          onScroll={(e) =>
            setFrameSheetScrolled((e.target as HTMLElement).scrollTop > 0)
          }
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "20px 20px 12px",
              position: "sticky",
              top: 0,
              zIndex: 10,
              backgroundColor: "var(--ion-color-medium-contrast)",
              boxShadow: frameSheetScrolled
                ? "0 2px 8px rgba(0,0,0,0.12)"
                : "none",
              transition: "box-shadow 0.2s",
            }}
          >
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
              Izaberite ram
            </h2>
            <IonIcon
              icon={closeOutline}
              style={{
                fontSize: 24,
                cursor: "pointer",
                color: "var(--ion-color-medium)",
              }}
              onClick={() => setShowFrameSheet(false)}
            />
          </div>
          <div style={{ padding: "0 20px 20px" }}>
            {(["none", "disassembled", "assembled"] as const)
              .filter((opt) => opt !== "assembled" || canHaveFrameAssembled)
              .map((opt, idx) => {
                const isSelected = selectedFrame === opt;
                const label = ["Opcija 1", "Opcija 2", "Opcija 3"][idx];
                return (
                  <div
                    key={opt}
                    onClick={() => {
                      setShowFrameSheet(false);
                      addToCartWithFrame(opt);
                    }}
                    style={{
                      padding: 12,
                      marginBottom: 8,
                      borderRadius: 12,
                      border: isSelected
                        ? "2px solid var(--ion-color-primary)"
                        : "1px solid var(--ion-color-light-shade)",
                      backgroundColor: isSelected
                        ? props.darkMode
                          ? "rgba(56, 128, 255, 0.18)"
                          : "rgba(56, 128, 255, 0.08)"
                        : "var(--ion-color-light)",
                      cursor: "pointer",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 10,
                      }}
                    >
                      <div
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: "50%",
                          border: `2px solid ${isSelected ? "var(--ion-color-primary)" : "var(--ion-color-medium)"}`,
                          backgroundColor: isSelected
                            ? "var(--ion-color-primary)"
                            : "transparent",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {isSelected && (
                          <div
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              backgroundColor: "white",
                            }}
                          />
                        )}
                      </div>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: isSelected ? 600 : 400,
                          color: isSelected
                            ? "var(--ion-color-primary)"
                            : "var(--ion-color-medium)",
                        }}
                      >
                        {label}
                      </span>
                    </div>

                    {opt === "none" && (
                      <>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            marginBottom: 8,
                          }}
                        >
                          <img
                            src={`assets/pics/320/PLATNO.jpg`}
                            alt="Bez rama"
                            style={{
                              height: 80,
                              borderRadius: 8,
                              objectFit: "contain",
                            }}
                          />
                        </div>
                        <div style={{ fontWeight: 600, fontSize: 15 }}>
                          Osnovni set za slikanje
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            color: "var(--ion-color-medium)",
                          }}
                        >
                          Urolano platno za slikanje, boje, četkice
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            marginTop: 4,
                          }}
                        >
                          <NumberMoney value={pic.price} />
                        </div>
                      </>
                    )}

                    {opt === "disassembled" && (
                      <>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 8,
                          }}
                        >
                          <img
                            src={`assets/pics/320/PLATNO.jpg`}
                            alt="Platno"
                            style={{
                              height: 80,
                              borderRadius: 8,
                              objectFit: "contain",
                            }}
                          />
                          <span style={{ fontSize: 22, fontWeight: 700 }}>
                            +
                          </span>
                          <img
                            src="assets/pics/320/Frame.jpg"
                            alt="Rasklopljen ram"
                            style={{
                              height: 80,
                              borderRadius: 8,
                              objectFit: "contain",
                            }}
                          />
                        </div>
                        <div style={{ fontWeight: 600, fontSize: 15 }}>
                          Osnovni set + rasklopljen (blind) ram
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            marginTop: 4,
                          }}
                        >
                          <NumberMoney value={pic.price + equipmentPrice.frame} />
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            marginTop: 8,
                          }}
                        >
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowBlindRamModal(true);
                            }}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              padding: "3px 10px",
                              borderRadius: 20,
                              fontSize: 14,
                              backgroundColor: "var(--ion-color-light-shade)",
                              color: "var(--ion-color-dark)",
                              cursor: "pointer",
                            }}
                          >
                            <IonIcon
                              icon={informationCircleOutline}
                              style={{ fontSize: 15 }}
                            />
                            šta je blind ram?
                          </span>
                        </div>
                      </>
                    )}

                    {opt === "assembled" && (
                      <>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 8,
                          }}
                        >
                          <img
                            src={`assets/pics/320/PLATNO.jpg`}
                            alt="Platno"
                            style={{
                              height: 80,
                              borderRadius: 8,
                              objectFit: "contain",
                            }}
                          />
                          <span style={{ fontSize: 22, fontWeight: 700 }}>
                            +
                          </span>
                          <img
                            src="assets/pics/320/FRAME-ASSEMBLED.jpg"
                            alt="Sklopljen ram"
                            style={{
                              height: 80,
                              borderRadius: 8,
                              objectFit: "contain",
                            }}
                          />
                        </div>
                        <div style={{ fontWeight: 600, fontSize: 15 }}>
                          Osnovni set montiran na ramu
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            marginTop: 4,
                          }}
                        >
                          <NumberMoney value={pic.price + equipmentPrice.frameAssembled} />
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            marginTop: 8,
                          }}
                        >
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowBlindRamModal(true);
                            }}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              padding: "3px 10px",
                              borderRadius: 20,
                              fontSize: 14,
                              backgroundColor: "var(--ion-color-light-shade)",
                              color: "var(--ion-color-dark)",
                              cursor: "pointer",
                            }}
                          >
                            <IonIcon
                              icon={informationCircleOutline}
                              style={{ fontSize: 15 }}
                            />
                            šta je blind ram?
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
          </div>
          <div
            style={{
              position: "sticky",
              bottom: 0,
              backgroundColor: "var(--ion-color-medium-contrast)",
              padding: "4px 20px 12px",
            }}
          >
            <IonButton
              expand="block"
              // fill="clear"
              color="medium"
              onClick={() => setShowFrameSheet(false)}
            >
              Odustani
            </IonButton>
          </div>
        </div>
      </IonModal>
      <IonModal
        isOpen={showBlindRamModal}
        onDidDismiss={() => setShowBlindRamModal(false)}
        className="cart-modal"
      >
        <div
          style={{
            overflowY: "auto",
            maxHeight:
              "calc(90dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
          }}
          onScroll={(e) =>
            setBlindRamScrolled((e.target as HTMLElement).scrollTop > 0)
          }
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "20px 20px 12px",
              position: "sticky",
              top: 0,
              zIndex: 10,
              backgroundColor: "var(--ion-color-medium-contrast)",
              boxShadow: blindRamScrolled
                ? "0 2px 8px rgba(0,0,0,0.12)"
                : "none",
              transition: "box-shadow 0.2s",
            }}
          >
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
              Šta je blind ram?
            </h2>
            <IonIcon
              icon={closeOutline}
              style={{
                fontSize: 24,
                cursor: "pointer",
                color: "var(--ion-color-medium)",
              }}
              onClick={() => setShowBlindRamModal(false)}
            />
          </div>
          <div style={{ padding: "0 20px 20px" }}>
            <IonChip
              style={{
                display: "flex",
                padding: "25px 20px",
                marginBottom: 10,
                marginTop: 5,
              }}
            >
              <IonIcon icon={tabletLandscape} size="large" color="dark" />
              <IonLabel className="f15">Blind ram</IonLabel>
              <div style={{ flexGrow: 1 }} />
              <NumberMoney value={equipmentPrice.frame} />
            </IonChip>
            <p className="f13 ion-text-justify">
              U ponudi imamo i BLIND ramove dimezija 40x50cm.
              <br />
              <br />
              BLIND RAM SE ŠALJE RASKLOPLJEN
              <br />
              <br />
              Sa BLIND ramom imate sve što vam je potrebno da obojenu sliku
              okačite na zid.
              <br />
              <br />
              Takođe, samo bojenje je dosta lakše kada je platno zategnuito na
              blind ramu.
              <br />
              Samo sastavljanje blind rama i zatezanje platna je vrlo
              jednostavno (možete videti video prezentaciju ispod).
              <br />
              <br />
              Uz BLIND ram dobijate:
            </p>
            <ul className="ion-text-left" style={{ fontSize: 13 }}>
              <li>4 lajsne</li>
              <li>8 kajlica za fiksiranje rama</li>
              <li>20 rajsnedli za kačenje platna</li>
              <li>Uputstvo za sklapanje rama i zatezanje platna</li>
            </ul>
            <div
              style={{
                position: "relative",
                height: 0,
                paddingBottom: "56.25%",
                marginBottom: 25,
                marginTop: 12,
                width: "100%",
              }}
            >
              <iframe
                src="https://www.youtube.com/embed/VNNnJVdsOSg"
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
              />
            </div>

            <IonChip
              style={{
                display: "flex",
                padding: "5px 20px",
                marginBottom: 10,
                marginTop: 20,
                maxHeight: "none",
                height: "auto",
              }}
            >
              <IonIcon
                icon={tabletLandscape}
                size="large"
                color="dark"
                style={{ flexShrink: 0 }}
              />
              <span
                className="f14"
                style={{
                  whiteSpace: "normal",
                  lineHeight: 1.4,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                Blind ram - sklopljen za zategnutim platnom (motivom)
              </span>
              <NumberMoney value={equipmentPrice.frameAssembled} />
            </IonChip>
            <p className="f13 ion-text-justify">
              Postoji i mogućnost uslužnog sklapanja blind rama i slanja platna
              zategnutog na blind ramu.
              <br />
              Cena usluge je{" "}
              <NumberMoney
                value={equipmentPrice.frameAssembled - equipmentPrice.frame}
              />
              .
            </p>
          </div>
          <div
            style={{
              position: "sticky",
              bottom: 0,
              backgroundColor: "var(--ion-color-medium-contrast)",
              padding: "4px 20px 12px",
            }}
          >
            <IonButton
              expand="block"
              // fill="clear"
              color="medium"
              onClick={() => setShowBlindRamModal(false)}
            >
              Zatvori
            </IonButton>
          </div>
        </div>
      </IonModal>
      <PackageContentModal
        isOpen={showPackageModal}
        onDismiss={() => setShowPackageModal(false)}
      />
    </IonCard>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(PictureCard);
